// src/pages/TermsAndConditions.jsx
import { Box, Container, Typography, Stack, Card, CardContent, Divider, Link as MUILink } from "@mui/material";

export default function TermsAndConditions() {
  return (
    <Box component="section" sx={{ py: { xs: 6, md: 8 }, backgroundColor: "background.default" }}>
      <Container maxWidth="md">
        {/* Header */}
        <Stack spacing={2} sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Legal
          </Typography>
          <Typography variant="h4" fontWeight={800}>Terms &amp; Conditions</Typography>
          <Typography color="text.secondary">Last updated: August 24, 2025</Typography>
        </Stack>

        {/* Body */}
        <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={3}>
              {/* 1 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  1. Acceptance of Terms
                </Typography>
                <Typography color="text.secondary">
                  Welcome to <strong>CosmetiCompare</strong> (“we”, “our”, “us”). By accessing or using
                  <span> https://www.cosmeticompare.com</span> (the “Website”), you agree to these Terms &amp; Conditions.
                  If you do not agree, please do not use the Website.
                </Typography>
              </section>

              <Divider />

              {/* 2 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  2. Nature of Our Service
                </Typography>
                <Typography color="text.secondary">
                  CosmetiCompare is an independent beauty product discovery and comparison platform.
                  We list and compare products and may redirect you to third‑party e‑commerce sites via affiliate links.
                  We are not the manufacturer, seller, or distributor of listed products.
                </Typography>
              </section>

              <Divider />

              {/* 3 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  3. Intellectual Property &amp; Images
                </Typography>
                <Typography color="text.secondary">
                  All product names, images, posters, brand logos, and trademarks displayed are the property of their
                  respective owners/companies. We do not claim ownership and display such materials solely for discovery
                  and comparison. <strong>Any commercial use</strong> (including reproduction or redistribution) of these
                  materials by any person requires <strong>prior permission from the original owner/brand</strong>.
                </Typography>
              </section>

              <Divider />

              {/* 4 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  4. Acceptable Use
                </Typography>
                <Typography color="text.secondary" component="div">
                  You agree to use the Website for personal, non‑commercial purposes and not to:
                  <ul style={{ marginTop: 8 }}>
                    <li>Copy, scrape, harvest, or extract product data, images, or content without written permission.</li>
                    <li>Use bots, automation, or any activity that disrupts service or circumvents security.</li>
                    <li>Reverse engineer, probe, or overload our infrastructure.</li>
                  </ul>
                </Typography>
              </section>

              <Divider />

              {/* 5 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  5. Affiliate Links &amp; Third‑Party Platforms
                </Typography>
                <Typography color="text.secondary">
                  We may earn a commission when you buy through affiliate links, at no extra cost to you.
                  Product details, pricing, availability, delivery, refunds, and customer service are governed by the
                  respective e‑commerce platform’s terms. We do not control or guarantee any third‑party content or services.
                </Typography>
              </section>

              <Divider />

              {/* 6 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  6. Accuracy &amp; Disclaimer
                </Typography>
                <Typography color="text.secondary">
                  We strive for accuracy but do not guarantee that prices, availability, descriptions, ratings, or other
                  information will always be current or error‑free. The Website is provided on an “as is” and “as available”
                  basis without warranties of any kind, express or implied.
                </Typography>
              </section>

              <Divider />

              {/* 7 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  7. Limitation of Liability
                </Typography>
                <Typography color="text.secondary">
                  To the maximum extent permitted by law, CosmetiCompare and its affiliates shall not be liable for any
                  direct, indirect, incidental, consequential, or special damages arising from your use of the Website,
                  reliance on information displayed, or any transaction with third parties accessed via the Website.
                </Typography>
              </section>

              <Divider />

              {/* 8 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  8. Privacy
                </Typography>
                <Typography color="text.secondary">
                  We currently do not collect personal data via the Website. For details, please see our{" "}
                  <MUILink href="/privacy-policy">Privacy Policy</MUILink>. If our practices change, we will update our
                  policies and obtain required consents.
                </Typography>
              </section>

              <Divider />

              {/* 9 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  9. Governing Law &amp; Jurisdiction (India)
                </Typography>
                <Typography color="text.secondary">
                  These Terms are governed by the laws of India. Courts in Bengaluru, Karnataka, shall have exclusive
                  jurisdiction over any disputes arising out of or related to these Terms or the Website.
                </Typography>
              </section>

              <Divider />

              {/* 10 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  10. Changes to Terms
                </Typography>
                <Typography color="text.secondary">
                  We may update these Terms &amp; Conditions from time to time. Changes are effective when posted on this page.
                  Continued use of the Website constitutes acceptance of the updated Terms.
                </Typography>
              </section>

              <Divider />

              {/* 11 */}
              <section>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  11. Contact Us
                </Typography>
                <Typography color="text.secondary">
                  Questions about these Terms? Email us at <strong>support@cosmeticompare.com</strong>.
                </Typography>
              </section>

              <Divider />

              {/* Footer note */}
              <Typography variant="body2" color="text.secondary">
                This page is for general information only and does not constitute legal advice.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
